import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { showExamBoard } from '@src/settings/search';
import { hasRefinement } from '@src/utils/search';
import { getSuggestionsForUser } from '@src/utils/searchSuggestions';
import { useCurrentRefinements, useSearchBox } from 'react-instantsearch';
import { UserData } from '@src/types/User/User';

export const useShowMoreInformation = () => {
  const currentFilters = useCurrentRefinements();
  const { query } = useSearchBox();

  const hasSelectedExamCourse = currentFilters.items.some((item) =>
    item.attribute.includes('doc_exam_course'),
  );

  const hasSelectedExamBoard = currentFilters.items.some((item) =>
    item.attribute.includes('doc_exam_board'),
  );

  // A course plus a resource type is specific enough to show results without a board, e.g.
  // the "A Level Past Papers" search suggestion, which arrives with both set from the URL.
  const hasSelectedResourceType = currentFilters.items.some((item) =>
    item.attribute.includes('doc_type'),
  );

  const showExamBoardFilter = hasRefinement(currentFilters.items, 'doc_exam_course', showExamBoard);

  return (
    Boolean(query) ||
    (hasSelectedExamCourse &&
      (showExamBoardFilter ? hasSelectedExamBoard || hasSelectedResourceType : true))
  );
};

export const useSearchSuggestions = () => {
  const user = useSelector((state: any) => state.user.data as UserData | null);
  const isLoading = useSelector((state: any) => state.user.isLoading as boolean);

  const isSignedIn = Boolean(user?.userId);
  const background = user?.userbackground;

  // Nothing until the user has loaded, so logged-in users don't see the defaults flash first.
  const { suggestions, isPersonalised } = useMemo(
    () =>
      isLoading
        ? { suggestions: [], isPersonalised: false }
        : getSuggestionsForUser({ userId: user?.userId, userbackground: background }),
    [isLoading, user?.userId, background],
  );

  return { background, isSignedIn, isPersonalised, suggestions };
};

export const useSortByPracticeType = () =>
  useCallback((a: any, b: any) => {
    const aHasMarkScheme = a.name.toLowerCase().includes('mark scheme');
    const bHasMarkScheme = b.name.toLowerCase().includes('mark scheme');

    if (aHasMarkScheme !== bHasMarkScheme) {
      return aHasMarkScheme ? 1 : -1;
    }

    return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
  }, []);

export const useSortByModules = () =>
  useCallback((a: any, b: any) => {
    const aHasOld = a.name.toLowerCase().includes('old');
    const bHasOld = b.name.toLowerCase().includes('old');

    if (aHasOld !== bHasOld) {
      return aHasOld ? 1 : -1;
    }

    return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
  }, []);
